import type { OrgRoles, Roles } from "@aura-frontend/omnibus-az-client";
import { Button, cn, HorizontalMenu, Icons } from "@win-reality/react-ui";
import { useMemo, useState } from "react";
import {
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
} from "react-router-dom";

import { useNavMenu } from "@/components/ui/SideMenu";
import { useAppContext } from "@/hooks/use-app-context";
import { ROUTES } from "@/lib/routes";

import { OrganizationSelect } from "./Players/OrganizationSelect";

export interface ManageLayoutContext {
  orgId: string | null;
  setOrgId: (orgId: string) => void;
}

export function useManageLayoutContext() {
  return useOutletContext<ManageLayoutContext>();
}

/** Forwards the ManageLayoutContext through nested Outlet levels */
export function ManageOutlet() {
  const context = useManageLayoutContext();
  return <Outlet context={context} />;
}

const baseTabs = [
  { label: "Operators", to: ROUTES.MANAGE_OPERATORS },
  { label: "Rosters", to: ROUTES.MANAGE_ROSTERS },
  { label: "Players", to: ROUTES.MANAGE_PLAYERS },
];

const adminOnlyTabs = [
  {
    label: "Organizations",
    to: ROUTES.MANAGE_ORGANIZATIONS,
    permissions: ["magnus_super_admin", "magnus_admin"] as const,
  },
];

const ManageLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen, setIsOpen } = useNavMenu();
  const { user, currentOrganization, orgId: globalOrgId } = useAppContext();
  const [localOrgId, setLocalOrgId] = useState<string | null>(globalOrgId);
  const isOrganizationsTab = location.pathname.startsWith(
    ROUTES.MANAGE_ORGANIZATIONS,
  );

  const currentRoles = useMemo(
    () =>
      [...(user?.roles || []), ...(currentOrganization?.orgRoles || [])] as (
        | Roles
        | OrgRoles
      )[],
    [user?.roles, currentOrganization?.orgRoles],
  );

  const tabs = useMemo(() => {
    const filtered = adminOnlyTabs.filter((tab) =>
      tab.permissions.some((p) => currentRoles.includes(p)),
    );
    return [...baseTabs, ...filtered];
  }, [currentRoles]);

  const outletContext = useMemo<ManageLayoutContext>(
    () => ({ orgId: localOrgId, setOrgId: setLocalOrgId }),
    [localOrgId],
  );

  return (
    <div className="flex flex-col h-full">
      <div className="p-5 flex justify-between">
        <div>
          <div className="shrink-0 md:hidden ">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(!isOpen)}
            >
              <Icons.Bars3BottomLeftOutline />
              <span className="sr-only">Toggle Menu</span>
            </Button>
          </div>
        </div>

        <div
          className={cn(isOrganizationsTab && "opacity-0 pointer-events-none")}
        >
          <OrganizationSelect value={localOrgId} onChange={setLocalOrgId} />
        </div>
      </div>

      <div className="md:pt-0 px-5">
        <div className="border border-b-gray-200 grow flex justify-center min-w-0">
          <HorizontalMenu
            size="md"
            wrapperClassName="w-full [&>*]:mx-auto"
            items={tabs.map((tab) => ({
              label: tab.label,
              active: location.pathname.startsWith(tab.to),
              onClick: () => navigate(tab.to),
            }))}
          />
        </div>
      </div>
      <div className="flex-1 py-8">
        <Outlet context={outletContext} />
      </div>
    </div>
  );
};

export { ManageLayout };
